/**
 * Global app color-mode constants, route-based defaults, and legacy key migration.
 */

import {
  AUDIENCE_SLUGS,
  MARKETING_SLUGS as FUSION_MSI_MARKETING_SLUGS,
  PLATFORM_SLUGS,
  isFusionMsiHost,
} from 'features/decisionPoint/marketing/fusionMsiMarketingRouting';
import { hasGeoCountry } from 'features/decisionPoint/marketing/geoMarketingData';
import {
  MARKETING_SLUGS as VOXPONT_MARKETING_SLUGS,
  isVoxPontMarketingHost,
} from 'pages/voxpontmarketing/voxpontMarketingRouting';
import { isClinicalHudMarketingPath } from 'features/clinicalHud/marketing/clinicalHudMarketingRouting';
import { isMd3cMarketingHost } from 'features/md3c/marketing/md3cMarketingRouting';

export const APP_COLOR_MODE_KEY = 'app.colorMode';
/** Session-scoped mode until the user explicitly toggles (survives marketing → app navigation). */
export const APP_SESSION_COLOR_MODE_KEY = 'app.sessionColorMode';
export const DECISION_POINT_COLOR_MODE_KEY = 'decisionPoint.colorMode';
export const VOXPONT_COLOR_MODE_KEY = 'voxpont.colorMode';

export const COLOR_MODE = Object.freeze({
  DARK: 'dark',
  LIGHT: 'light',
});

export const COLOR_MODE_EVENT = 'app:colorModeChange';

/** FusionMSI operational paths that default to light when no stored preference. */
const FUSION_MSI_APP_PREFIXES = [
  '/dp-auth',
  '/dp-join',
  '/dp-display',
  '/dp-review',
  '/dp-admin',
  '/dp-import',
  '/dp-coverage',
  '/dp-record',
  '/dp-sources',
  '/auth',
  '/signup',
  '/join',
  '/admin',
  '/review',
  '/display',
  '/import',
  '/coverage',
  '/sources',
];

function normalizeMode(value) {
  if (value === COLOR_MODE.LIGHT) return COLOR_MODE.LIGHT;
  if (value === COLOR_MODE.DARK) return COLOR_MODE.DARK;
  return null;
}

function readStoredMode(key) {
  try {
    return normalizeMode(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

/**
 * True when pathname is a public marketing surface that defaults to dark.
 * md3c.com is the sibling host and defaults to light (same light/dark palettes).
 */
export function isMarketingDefaultDark(pathname = '') {
  if (typeof pathname !== 'string' || !pathname) return false;

  // md3c.com shares FusionMSI colors but defaults to light mode.
  if (isMd3cMarketingHost()) return false;

  if (isFusionMsiMarketingPath(pathname)) return true;
  if (isVoxPontMarketingPath(pathname)) return true;
  if (isClinicalHudMarketingPath(pathname)) return true;
  return false;
}

function isFusionMsiOperationalPath(pathname) {
  return FUSION_MSI_APP_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function isFusionMsiMarketingPath(pathname) {
  if (isFusionMsiOperationalPath(pathname)) return false;

  if (isFusionMsiHost()) {
    if (pathname === '/' || pathname === '') return true;
    const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);
    const segment = parts[0];
    if (!segment) return true;
    if (
      AUDIENCE_SLUGS.includes(segment) ||
      FUSION_MSI_MARKETING_SLUGS.includes(segment) ||
      PLATFORM_SLUGS.includes(segment) ||
      segment === 'about' ||
      segment === 'business-model' ||
      segment === 'platform' ||
      hasGeoCountry(segment)
    ) {
      return true;
    }
    if (parts.length === 2 && hasGeoCountry(parts[0])) {
      return true;
    }
    return false;
  }

  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/dp') return true;
  if (normalized === '/dp/about') return true;
  if (normalized === '/dp/business-model') return true;
  if (normalized.startsWith('/dp/platform')) return true;

  const dpParts = normalized.replace(/^\/dp\/?/, '').split('/').filter(Boolean);
  const dpSegment = dpParts[0];
  if (
    AUDIENCE_SLUGS.includes(dpSegment) ||
    FUSION_MSI_MARKETING_SLUGS.includes(dpSegment)
  ) {
    return true;
  }
  if (hasGeoCountry(dpSegment)) {
    return true;
  }

  return false;
}

function isVoxPontMarketingPath(pathname) {
  if (/^\/voxpont\d+$/.test(pathname)) return false;

  if (pathname === '/voxpontmarketing' || pathname.startsWith('/voxpontmarketing/')) {
    return true;
  }

  if (pathname === '/voxpont' || pathname.startsWith('/voxpont/')) {
    const segment = pathname.replace(/^\/voxpont\/?/, '').split('/')[0];
    if (!segment || segment === 'brand' || VOXPONT_MARKETING_SLUGS.includes(segment)) {
      return true;
    }
    return false;
  }

  if (isVoxPontMarketingHost()) {
    if (pathname === '/' || pathname === '') return true;
    const segment = pathname.replace(/^\/+/, '').split('/')[0];
    return VOXPONT_MARKETING_SLUGS.includes(segment);
  }

  return false;
}

export function resolveDefaultMode(pathname = '') {
  return isMarketingDefaultDark(pathname) ? COLOR_MODE.DARK : COLOR_MODE.LIGHT;
}

function readSessionMode() {
  try {
    return normalizeMode(window.sessionStorage.getItem(APP_SESSION_COLOR_MODE_KEY));
  } catch {
    return null;
  }
}

function writeSessionMode(mode) {
  const next = normalizeMode(mode);
  if (!next) return;
  if (readSessionMode() === next) return;
  try {
    window.sessionStorage.setItem(APP_SESSION_COLOR_MODE_KEY, next);
  } catch {
    /* no-op */
  }
}

/**
 * Pure read for useSyncExternalStore — no storage writes.
 */
export function peekColorMode(pathname = '') {
  const persisted = readPersistedMode();
  if (persisted) return persisted;

  const sessionMode = readSessionMode();
  if (sessionMode) return sessionMode;

  return resolveDefaultMode(pathname);
}

/** Persist session-scoped default when the user has not chosen a mode yet. */
export function ensureSessionColorMode(pathname = '') {
  if (readPersistedMode() || readSessionMode()) {
    return peekColorMode(pathname);
  }
  const initial = resolveDefaultMode(pathname);
  writeSessionMode(initial);
  syncDocumentColorMode(initial);
  return initial;
}

/**
 * Reads persisted mode: app key first, then legacy keys (migrate on read).
 */
export function readPersistedMode() {
  const appMode = readStoredMode(APP_COLOR_MODE_KEY);
  if (appMode) return appMode;

  const dpMode = readStoredMode(DECISION_POINT_COLOR_MODE_KEY);
  const vpMode = readStoredMode(VOXPONT_COLOR_MODE_KEY);
  const legacy = dpMode || vpMode;
  if (legacy) {
    try {
      window.localStorage.setItem(APP_COLOR_MODE_KEY, legacy);
    } catch {
      /* no-op */
    }
    return legacy;
  }

  return null;
}

export function writePersistedMode(mode) {
  const next = normalizeMode(mode) ?? COLOR_MODE.LIGHT;
  writeSessionMode(next);
  try {
    window.localStorage.setItem(APP_COLOR_MODE_KEY, next);
    window.localStorage.setItem(DECISION_POINT_COLOR_MODE_KEY, next);
    window.localStorage.setItem(VOXPONT_COLOR_MODE_KEY, next);
  } catch (err) {
    console.warn('[AppColorMode] could not persist color mode', err);
  }
  syncDocumentColorMode(next);
  try {
    window.dispatchEvent(new CustomEvent(COLOR_MODE_EVENT, { detail: next }));
  } catch {
    /* no-op */
  }
  return next;
}

export function syncDocumentColorMode(mode) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.colorMode = mode;
}

export function resolveColorMode(pathname = '') {
  ensureSessionColorMode(pathname);
  return peekColorMode(pathname);
}
